import {
  type DateOnly,
  type FixingIndexMarkMessageData,
  getMarketDataSnapshotMid,
  type MarketDataSnapshot,
  useBehaviorSubject,
  type useMultiMarketDataSnapshot,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { combineLatest, map, type Observable, startWith } from 'rxjs';
import { useIndexMarksOnDate } from '../../../utils/indexMarkUtils';
import type { Fixing } from './fixingSlice';

export interface FixingBlotterRow {
  fixing: Fixing;
  rowID: string;
  fixingDate: DateOnly | undefined;
  midPrice: string | undefined;
  indexMark: FixingIndexMarkMessageData | undefined;
}

/**
 * Builds the Fixing Blotter data stream by combining current fixings, market-data mids, and index marks into table rows.
 */
export const useFixingBlotterDataObs = (
  fixings: Fixing[],
  multiMarketDataObs: ReturnType<typeof useMultiMarketDataSnapshot>,
  fixingDate: DateOnly | undefined
): Observable<{ data: FixingBlotterRow[] }> => {
  const indexMarksByFixingName = useIndexMarksOnDate(fixingDate);
  const { observable: fixingsObs } = useBehaviorSubject(() => fixings, [fixings]);
  const { observable: indexMarksByFixingNameObs } = useBehaviorSubject(
    () => indexMarksByFixingName,
    [indexMarksByFixingName]
  );

  // Pull out the "cache" (just an absolute map of symbol -> MDS) and start with empty map so we never have to wait for market data here
  const mdsMapObs = useMemo(
    () =>
      multiMarketDataObs.pipe(
        map(value => value.cache),
        startWith(new Map<string, MarketDataSnapshot>())
      ),
    [multiMarketDataObs]
  );

  return useMemo(() => {
    return combineLatest([fixingsObs, mdsMapObs, indexMarksByFixingNameObs]).pipe(
      map(([fixings, mdsMap, indexMarksByFixingName]) => {
        return {
          initial: true,
          data: fixings.map(fixing => {
            const mark = indexMarksByFixingName.get(fixing.fixingIndex.Name);
            return {
              rowID: fixing.fixingIndex.Name,
              fixing,
              fixingDate,
              midPrice: fixing.symbol ? getMarketDataSnapshotMid(mdsMap.get(fixing.symbol)) : undefined,
              indexMark: mark,
            } satisfies FixingBlotterRow;
          }),
        };
      })
    );
  }, [fixingsObs, fixingDate, mdsMapObs, indexMarksByFixingNameObs]);
};
